import { useCallback, useEffect, useRef, useState } from "react";

import { fetchFeedback } from "./api";
import type { FeedbackItem } from "./types";

const POLL_INTERVAL_MS = 30_000;

export interface UseFeedbackReturn {
  feedback: FeedbackItem[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  lastRefreshedAt: Date | null;
}

/**
 * Loads the operator feedback list and polls every 30s (paused while the tab is
 * hidden). Read-only: the endpoint returns newest-first, so there is no client
 * sort or mutation, mirroring the waitlist hook minus the write paths.
 */
export function useFeedback(): UseFeedbackReturn {
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Stale-response guard: overlapping fetches (poll + manual refresh) or a
  // request resolving after unmount must not apply their result. Only the
  // latest request, on a still-mounted hook, commits state.
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isCurrent = (): boolean => mountedRef.current && requestId === requestIdRef.current;
    try {
      const data = await fetchFeedback();
      if (!isCurrent()) return;
      setFeedback(data);
      setError(null);
      setLastRefreshedAt(new Date());
    } catch (e) {
      if (!isCurrent()) return;
      setError(e instanceof Error ? e.message : "Failed to load feedback");
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    setIsLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    void load();
    intervalRef.current = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
    };
  }, [load]);

  useEffect(() => {
    function handleVisibilityChange(): void {
      if (document.hidden) {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        void load();
        if (intervalRef.current !== null) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => void load(), POLL_INTERVAL_MS);
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [load]);

  return { feedback, isLoading, error, refresh, lastRefreshedAt };
}
